import type { PropsWithChildren } from 'react'
import { useState } from 'react'

import '@mobiscroll/react/dist/css/mobiscroll.scss'
import { MdOutlineArrowBackIos, MdOutlineArrowForwardIos } from 'react-icons/md'
import styled from 'styled-components'
import {
  Eventcalendar,
  CalendarNav,
  CalendarPrev,
  CalendarNext,
} from '@mobiscroll/react'

import { type SelectedTile } from 'Components/CalendarModal'

import CalendarTile, { type CalendarTileProps } from './CalendarTile'

import { format, isSameDay, differenceInCalendarMonths } from 'date-fns'
import isEmpty from 'lodash.isempty'

const Wrap = styled.div`
  padding: 0 150px;

  .mbsc-font {
    font-family: 'Gotham SSm A', 'Gotham SSm B', Helvetica, Arial, sans-serif;
    color: #454545;
  }

  .mbsc-ios.mbsc-calendar {
    margin: -30px;
    padding-top: 30px;
  }

  .mbsc-calendar-header.mbsc-calendar-header {
    background-color: #ffffff;
    margin: 30px 30px -1px;
  }

  .mbsc-ios.mbsc-calendar-controls {
    padding-bottom: 30px;
  }

  .mbsc-calendar-width-md .mbsc-calendar-day-labels .mbsc-calendar-day-inner {
    text-align: center;

    .mbsc-calendar-day-text {
      font-size: 16px;
      font-weight: 700;
      color: #454545;
      opacity: 1;
      position: relative;
      z-index: 2;
    }

    .mbsc-calendar-month-name {
      display: none;
    }
  }

  .mbsc-calendar-day-outer .mbsc-calendar-month-name {
    color: #454545;
    opacity: 1;
  }

  .mbsc-eventcalendar .mbsc-calendar-week-days {
    background: #f7f7f7;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    padding: 4px 0;
    box-shadow: 0 0 8px rgb(0 0 0 / 15%);
  }

  .mbsc-calendar-height-md.mbsc-calendar-width-md
    .mbsc-calendar-week-day.mbsc-ltr {
    text-align: center;
    font-size: 16px;
    font-weight: 700;
    color: #454545;
  }

  .mbsc-hover .mbsc-calendar-cell-text {
    background-color: #ffffff;
  }

  .mbsc-selected .mbsc-calendar-cell-text {
    border-color: transparent;
    background: transparent;
    color: #000000;
  }

  .mbsc-calendar-row,
  .mbsc-calendar-cell {
    cursor: default;

    .mbsc-calendar-day-outer {
      background: #f7f7f7;
      .mbsc-calendar-day-text {
        color: #cccccc !important;
      }

      &:hover {
        .mbsc-calendar-cell-text {
          background: transparent;
        }
      }
    }
  }

  .mbsc-calendar-labels,
  .mbsc-calendar-text {
    display: none;
  }

  .mbsc-calendar-body {
    margin: 0 30px 30px;
    box-shadow: 0 2px 8px rgb(0 0 0 / 15%);
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  }

  .mbsc-ios .mbsc-calendar-wrapper {
    border-color: transparent;
  }
`

const CalendarNavWrap = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;

  .mbsc-eventcalendar .mbsc-calendar-header {
    background: #fff;
  }

  div[aria-live='polite'] .mbsc-calendar-button.mbsc-button {
    min-width: 155px;
  }

  .mbsc-calendar-button.mbsc-button {
    margin: 0 30px;

    .mbsc-calendar-title {
      font-size: 14px;
      font-weight: 700;
      color: #454545;
    }
  }

  .mbsc-icon {
    width: auto;
    height: auto;
    line-height: inherit;
  }
`

const NavigatorButton = styled.div<{ $active?: boolean }>`
  border: none;
  background: ${(props) => (props.$active ? '#F6F5FF' : '#eeeeee')};
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  cursor: ${(props) => (props.$active ? 'pointer' : 'normal')};

  svg {
    color: ${(props) => (props.$active ? '#4f31ff' : '#333333')};
    width: 20px;
    height: 20px;
  }
`

const DayContentWrap = styled.div`
  margin: 2px auto 8px;
  position: relative;
  z-index: 2;
  padding: 0 6px 0 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TodayIdicator = styled.div`
  position: absolute;
  background: #fbfbff;
  border-top: 5px solid #4f31ff;
  height: calc(100% + 8px);
  width: 101%;
  top: -6px;
`

export interface CalendarProps {
  defaultSelectedDate?: Date
  events: {
    id: string
    start: string
    end: string
    tileData: CalendarTileProps
    isReadOnly: boolean
  }[]
  tileClick: (selectedTile: SelectedTile) => void
  onSelectedDateChange?: (changedDate: Date) => void
  isReadOnly: boolean
}

function Calendar({
  defaultSelectedDate,
  events,
  tileClick,
  onSelectedDateChange,
  isReadOnly,
}: PropsWithChildren<CalendarProps>) {
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date>(
    defaultSelectedDate || new Date()
  )

  const isSelectedCalendarDateAfterCurrentMonth =
    differenceInCalendarMonths(selectedCalendarDate, new Date()) > 0 || false

  return (
    <Wrap>
      <Eventcalendar
        defaultSelectedDate={defaultSelectedDate || new Date()}
        onSelectedDateChange={(args) => {
          setSelectedCalendarDate(args.date as Date)
          onSelectedDateChange?.(args.date as Date)
        }}
        data={events}
        themeVariant="light"
        view={{
          calendar: {
            type: 'month',
            labels: 'all',
            outerDays: false,
          },
        }}
        height={960}
        renderHeader={() => (
          <CalendarNavWrap>
            <NavigatorButton $active={isSelectedCalendarDateAfterCurrentMonth}>
              <>
                <CalendarPrev />
                <MdOutlineArrowBackIos />
              </>
            </NavigatorButton>
            <CalendarNav />
            <NavigatorButton $active data-testid="next-month">
              <>
                <CalendarNext />
                <MdOutlineArrowForwardIos />
              </>
            </NavigatorButton>
          </CalendarNavWrap>
        )}
        renderDayContent={({ events, date }) => {
          const isToday = date && isSameDay(date, new Date())
          return (
            <>
              {isToday && <TodayIdicator />}
              <DayContentWrap>
                {events?.map((e) =>
                  !isEmpty(e.tileData) ? (
                    <CalendarTile
                      key={e.id}
                      {...e.tileData}
                      data-testid={`tile-${e.id}`}
                      onClick={() => {
                        if (e.id) {
                          const tileClickParams = {
                            id: e.id.toString(),
                            date: format(
                              new Date(e.start as Date),
                              'MMM d yyyy'
                            ),
                            time: e.tileData.time,
                            platform: e.tileData.platform,
                            status: e.tileData.status,
                          }
                          tileClick(tileClickParams)
                        }
                      }}
                      isReadOnly={isReadOnly}
                    />
                  ) : null
                )}
              </DayContentWrap>
            </>
          )
        }}
      />
    </Wrap>
  )
}

export default Calendar
